import React, { useState, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { PulseLoader } from 'react-spinners';

const API_URL = process.env.REACT_APP_API_URL;

function tryParseJson(value) {
    if (typeof value !== 'string') return value;
    try {
        return JSON.parse(value);
    } catch {
        return value;
    }
}

function parseApiResponse(raw) {
    let responseData = tryParseJson(raw);
    for (let i = 0; i < 4; i += 1) {
        if (!responseData || typeof responseData !== 'object') break;
        if (typeof responseData.body === 'string') {
            const parsedBody = tryParseJson(responseData.body);
            if (parsedBody && typeof parsedBody === 'object') {
                responseData = parsedBody;
                continue;
            }
        }
        if (responseData.body && typeof responseData.body === 'object' && (
            responseData.body.requiresConfirmation != null ||
            responseData.body.success != null ||
            responseData.body.data != null
        )) {
            responseData = responseData.body;
            continue;
        }
        if (typeof responseData.data === 'string') {
            const parsedData = tryParseJson(responseData.data);
            if (parsedData && typeof parsedData === 'object') {
                responseData = {
                    ...responseData,
                    data: parsedData,
                    requiresConfirmation:
                        responseData.requiresConfirmation ?? parsedData.requiresConfirmation,
                    message: responseData.message || parsedData.message,
                    success: responseData.success ?? parsedData.success
                };
            }
        }
        break;
    }
    return responseData;
}

function collectResponseLayers(responseData) {
    const layers = [];
    const visit = (node, depth = 0) => {
        if (!node || typeof node !== 'object' || Array.isArray(node) || depth > 4) return;
        layers.push(node);
        visit(node.data, depth + 1);
        visit(node.body, depth + 1);
        visit(node.result, depth + 1);
    };
    visit(responseData);
    return layers;
}

function normalizeConfirmationType(value) {
    return String(value || '').trim().toLowerCase();
}

function getConfirmationInfo(responseData) {
    const layers = collectResponseLayers(responseData);
    let requiresConfirmation = false;
    let confirmationType = '';
    let confirmField = '';
    let warnings = [];
    let unmatchedRows = [];
    let duplicateCount = 0;
    let newRecordCount = 0;
    let message = '';

    layers.forEach((layer) => {
        if (isTruthyFlag(layer.requiresConfirmation) || isTruthyFlag(layer.confirmRequired)) {
            requiresConfirmation = true;
        }
        if (layer.confirmationType) {
            confirmationType = normalizeConfirmationType(layer.confirmationType);
        }
        if (layer.confirmField) {
            confirmField = String(layer.confirmField);
        }
        if (Array.isArray(layer.warnings) && layer.warnings.length > 0) {
            warnings = layer.warnings;
        }
        if (Array.isArray(layer.unmatchedRows) && layer.unmatchedRows.length > 0) {
            unmatchedRows = layer.unmatchedRows;
        }
        const nextDuplicateCount = Number(layer.duplicateCount);
        if (!Number.isNaN(nextDuplicateCount) && nextDuplicateCount > 0) {
            duplicateCount = nextDuplicateCount;
        }
        const nextNewCount = Number(layer.newRecordCount);
        if (!Number.isNaN(nextNewCount) && nextNewCount > 0) {
            newRecordCount = nextNewCount;
        }
        if (layer.message) {
            message = String(layer.message);
        }
    });

    if (!duplicateCount) {
        duplicateCount = warnings.length;
    }
    if (!newRecordCount) {
        newRecordCount = unmatchedRows.length;
    }

    const isDuplicate =
        confirmationType === 'duplicate' ||
        confirmationType === 'duplicates' ||
        confirmField === 'confirmDuplicates' ||
        duplicateCount > 0 ||
        warnings.length > 0 ||
        (confirmationType !== 'addnew' && /already exist/i.test(message));

    const isAddNew =
        !isDuplicate &&
        (confirmationType === 'addnew' ||
            confirmField === 'confirmAddNew' ||
            newRecordCount > 0);

    return {
        requiresConfirmation,
        confirmationType,
        confirmField,
        warnings,
        unmatchedRows,
        duplicateCount,
        newRecordCount,
        message,
        isAddNew: requiresConfirmation && isAddNew,
        shouldConfirm: isDuplicate
    };
}

function isTruthyFlag(value) {
    return value === true || value === 'true' || value === 1 || value === '1';
}

function isApiSuccess(responseData) {
    if (!responseData || typeof responseData !== 'object') return false;
    const success = responseData.success;
    return success === true || success === 'true' || success === 1 || success === '1';
}

function getResultData(responseData) {
    return responseData?.data && typeof responseData.data === 'object'
        ? responseData.data
        : responseData || {};
}

const TrackerUpdateAdd = () => {
    const navigate = useNavigate();
    const [selectedFile, setSelectedFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingAction, setLoadingAction] = useState('');
    const [importResults, setImportResults] = useState(null);
    const [resultMode, setResultMode] = useState('');
    const fileInputRef = useRef(null);
    const [isDownloadingSample, setIsDownloadingSample] = useState(false);
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [duplicateWarnings, setDuplicateWarnings] = useState([]);
    const [duplicateCount, setDuplicateCount] = useState(0);
    const [modalMode, setModalMode] = useState('duplicate');
    const pendingUpdatePayloadRef = useRef(null);

    const extractDuplicateWarnings = (responseData) => {
        const confirmation = getConfirmationInfo(responseData);
        return {
            list: confirmation.warnings || [],
            count: confirmation.duplicateCount || (confirmation.warnings || []).length,
            data: getResultData(responseData)
        };
    };

    const closeDuplicateModal = () => {
        setShowDuplicateModal(false);
        setDuplicateWarnings([]);
        setDuplicateCount(0);
        setModalMode('duplicate');
    };

    // Convert file to base64
    const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            // Check if file is valid
            if (!file) {
                reject(new Error("No file selected"));
                return;
            }

            // Check file size (limit to 10MB)
            const maxSize = 10 * 1024 * 1024; // 10MB
            if (file.size > maxSize) {
                reject(new Error("File size exceeds 10MB limit"));
                return;
            }

            if (file.size === 0) {
                reject(new Error("File is empty or cannot be read"));
                return;
            }

            const reader = new FileReader();
            
            reader.onload = () => {
                try {
                    // Remove data URL prefix
                    const base64String = reader.result.split(',')[1];
                    if (!base64String) {
                        reject(new Error("Failed to convert file to base64"));
                        return;
                    }
                    resolve(base64String);
                } catch (error) {
                    reject(new Error("Error processing file: " + error.message));
                }
            };
            
            reader.onerror = (error) => {
                let errorMessage = "Failed to read file. ";
                if (error.target && error.target.error) {
                    const errorCode = error.target.error.code;
                    const NOT_FOUND_ERR = 1;
                    const NOT_READABLE_ERR = 2;
                    const ABORT_ERR = 3;
                    
                    switch (errorCode) {
                        case NOT_FOUND_ERR:
                            errorMessage += "File not found.";
                            break;
                        case NOT_READABLE_ERR:
                            errorMessage += "File is not readable. Please ensure the file is not open in another program and try again.";
                            break;
                        case ABORT_ERR:
                            errorMessage += "File reading was aborted.";
                            break;
                        default:
                            errorMessage += "An error occurred while reading the file. Please try selecting the file again.";
                    }
                } else {
                    errorMessage += "Please try selecting the file again.";
                }
                reject(new Error(errorMessage));
            };

            reader.onabort = () => {
                reject(new Error("File reading was aborted"));
            };

            try {
                reader.readAsDataURL(file);
            } catch (error) {
                reject(new Error("Failed to start reading file: " + error.message));
            }
        });
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Check if file is CSV or XLSX format
            const validExtensions = ['.csv', '.xlsx'];
            const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
            
            if (!validExtensions.includes(fileExtension)) {
                toast.error("Please upload a CSV or XLSX file (.csv, .xlsx)", { autoClose: 2000 });
                event.target.value = '';
                setSelectedFile(null);
                return;
            }

            setSelectedFile(file);
            setImportResults(null);
            setResultMode('');
            closeDuplicateModal();
            pendingUpdatePayloadRef.current = null;
        }
    };

    const prepareFilePayload = async () => {
        if (!selectedFile) {
            toast.error("Please select a CSV or XLSX file", { autoClose: 2000 });
            return null;
        }

        const userId = localStorage.getItem('id');
        if (!userId) {
            toast.error("User session expired. Please login again.", { autoClose: 2000 });
            setTimeout(() => {
                navigate('/');
            }, 2000);
            return null;
        }

        try {
            const fileBase64 = await fileToBase64(selectedFile);
            return {
                userid: userId,
                fileBase64,
                fileName: selectedFile.name || 'tracker.xlsx'
            };
        } catch (fileError) {
            toast.error(fileError.message || "Failed to read file. Please try again.", { autoClose: 3000 });
            setSelectedFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            return null;
        }
    };

    const resetFileInput = () => {
        setSelectedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        pendingUpdatePayloadRef.current = null;
        await applyExcelAction({
            action: 'update-tracker-from-excel',
            confirmDuplicates: false,
            resultMode: 'update',
            loadingKey: 'upload'
        });
    };

    const openConfirmationModal = ({ mode, list, count }) => {
        setModalMode(mode);
        setDuplicateWarnings(list);
        setDuplicateCount(count);
        setShowDuplicateModal(true);
    };

    const showDuplicatePromptFromResponse = (responseData) => {
        const confirmation = getConfirmationInfo(responseData);
        if (!confirmation.shouldConfirm) return false;
        const { list } = extractDuplicateWarnings(responseData);
        const warningList = (list && list.length ? list : confirmation.warnings) || [];
        const count = confirmation.duplicateCount || warningList.length;
        openConfirmationModal({
            mode: 'duplicate',
            list: warningList,
            count
        });
        return true;
    };

    const applyExcelAction = async ({
        action,
        confirmDuplicates = false,
        confirmAddNew = false,
        resultMode: nextResultMode = 'update',
        loadingKey = 'update'
    } = {}) => {
        const isConfirming = confirmDuplicates || confirmAddNew;
        setIsLoading(true);
        setLoadingAction(isConfirming ? 'confirm' : loadingKey);
        if (!isConfirming) {
            setImportResults(null);
            setResultMode('');
        }

        try {
            let filePayload = pendingUpdatePayloadRef.current;
            if (!filePayload) {
                filePayload = await prepareFilePayload();
                if (!filePayload) {
                    return;
                }
                pendingUpdatePayloadRef.current = filePayload;
            }

            const payload = {
                action,
                ...filePayload
            };
            if (confirmDuplicates) {
                payload.confirmDuplicates = true;
            }
            if (confirmAddNew) {
                payload.confirmAddNew = true;
            }

            let response = await axios.post(API_URL, payload);
            let responseData = parseApiResponse(response.data);
            let confirmation = getConfirmationInfo(responseData);

            if (!isConfirming && showDuplicatePromptFromResponse(responseData)) {
                return;
            }

            if (!isConfirming && confirmation.isAddNew) {
                response = await axios.post(API_URL, {
                    ...payload,
                    confirmAddNew: true
                });
                responseData = parseApiResponse(response.data);
                confirmation = getConfirmationInfo(responseData);
            }

            const resultData = getResultData(responseData);
            const updatedCount =
                Number(resultData.updated || 0) +
                Number(resultData.created || 0) +
                Number(resultData.imported || 0);

            if (isApiSuccess(responseData)) {
                setImportResults({
                    ...resultData,
                    resultMessage: responseData.message || resultData.message || ''
                });
                setResultMode(nextResultMode);
                const failedCount = Number(resultData.failed || 0);
                if (failedCount > 0 && updatedCount === 0) {
                    toast.error(responseData.message || 'No records were saved', { autoClose: 2500 });
                } else {
                    toast.success(
                        responseData.message ||
                            (nextResultMode === 'upload'
                                ? 'Import completed successfully'
                                : 'Update completed successfully'),
                        { autoClose: 2500 }
                    );
                }
                closeDuplicateModal();
                pendingUpdatePayloadRef.current = null;
                resetFileInput();
            } else {
                toast.error(responseData.message || 'Request failed', { autoClose: 2000 });
            }
        } catch (error) {
            console.error('Excel action error:', error);
            const errorData = error.response?.data ? parseApiResponse(error.response.data) : null;
            if (!isConfirming && errorData && showDuplicatePromptFromResponse(errorData)) {
                return;
            }
            toast.error(errorData?.message || 'An error occurred while processing the file', { autoClose: 2000 });
        } finally {
            setIsLoading(false);
            setLoadingAction('');
        }
    };

    const handleUpdate = async () => {
        pendingUpdatePayloadRef.current = null;
        await applyExcelAction({
            action: 'update-tracker-from-excel',
            confirmDuplicates: false,
            resultMode: 'update',
            loadingKey: 'update'
        });
    };

    const handleConfirmDuplicates = async () => {
        await applyExcelAction({
            action: 'update-tracker-from-excel',
            confirmDuplicates: true,
            resultMode: 'update',
            loadingKey: 'confirm'
        });
    };

    const handleCancelDuplicates = () => {
        closeDuplicateModal();
        pendingUpdatePayloadRef.current = null;
    };

    const handleDownloadSample = async () => {
        if (isDownloadingSample) return;
        setIsDownloadingSample(true);

        try {
            // Prefer template alias; backend resolves to the tracker sample object.
            // Alternative: { action, s3Key: "Supplier-attachment/DeliveryTracker.xlsx" }
            const payload = {
                action: 'get-download-presigned-url-for-object',
                template: 'tracker'
            };

            const response = await axios.post(API_URL, payload);
            const responseData = parseApiResponse(response.data);
            const downloadUrl =
                responseData?.downloadUrl ||
                responseData?.data?.downloadUrl ||
                responseData?.url ||
                responseData?.data?.url;

            if (!responseData?.success || !downloadUrl) {
                toast.error(responseData?.message || 'Failed to get download URL', {
                    autoClose: 2000
                });
                return;
            }

            const link = document.createElement('a');
            link.href = downloadUrl;
            link.rel = 'noopener noreferrer';
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Sample download error:', error);
            const errorData = error.response?.data
                ? parseApiResponse(error.response.data)
                : null;
            toast.error(
                errorData?.message ||
                    'An error occurred while downloading sample file',
                { autoClose: 2000 }
            );
        } finally {
            setIsDownloadingSample(false);
        }
    };

    return (
        <div>
            <div className="content-header">
                <div className="container-fluid">
                <div className="row mb-2 align-items-center">
                <div className="col-6 col-sm-6">
                            <h1 className="m-0 txt_lft">Update Customer Report </h1>
                        </div>
                        <div className="col-6 col-sm-6">
                            <ol className="breadcrumb justify-content-end">
                                <li className="breadcrumb-item"><Link to="/trackerUpdate"><i className="fa-solid fa-arrow-left-long" /> Back</Link></li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="frmwdth_cntr_640">
                                <div className="col-lg-12">
                                    <div className="card card-primary card-outline">
                                        <div className="card-header">
                                            <h3 className="card-title">Upload CSV File</h3>
                                        </div>
                                        <form className="form-horizontal" onSubmit={handleSubmit}>
                                            <div className="card-body">
                                                <div className="form-group">
                                                    <div className="input-group">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={selectedFile ? selectedFile.name : ''}
                                                            placeholder="Choose file"
                                                            readOnly
                                                        />
                                                        <div className="input-group-append">
                                                            <label className="btn btn-blue mb-0" htmlFor="trackerCsv" style={{ cursor: 'pointer' }}>
                                                                Browse
                                                            </label>
                                                            <input
                                                                ref={fileInputRef}
                                                                type="file"
                                                                className="d-none"
                                                                id="trackerCsv"
                                                                accept=".csv,.xlsx"
                                                                onChange={handleFileChange}
                                                            />
                                                        </div>
                                                    </div>
                                                    <small className="form-text text-muted mt-2">
                                                        CSV and XLSX files (.csv, .xlsx) are supported.
                                                        Use <strong>Upload</strong> to import new records and <strong>Update</strong> to update existing tracker records.
                                                    </small>
                                                    <div className="mt-2">
                                                        <button
                                                            type="button"
                                                            className="btn btn-link p-0"
                                                            onClick={handleDownloadSample}
                                                            disabled={isDownloadingSample}
                                                        >
                                                            {isDownloadingSample ? "Preparing download..." : "Download Sample Excel format"}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                            {/* /.card-body */}
                                            <div className="card-footer text-center">
                                                {isLoading ? (
                                                    <div className="d-flex justify-content-center align-items-center">
                                                        <PulseLoader color="#007bff" size={10} />
                                                        <span className="ml-2">
                                                            {loadingAction === 'confirm'
                                                                ? 'Updating duplicates...'
                                                                : loadingAction === 'update'
                                                                    ? 'Checking file...'
                                                                    : 'Importing...'}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <div className="d-flex justify-content-center flex-wrap" style={{ gap: '10px' }}>
                                                        <button
                                                            type="submit"
                                                            className="btn btn-blue"
                                                            disabled={!selectedFile || isLoading}
                                                        >
                                                            Upload
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-primary"
                                                            onClick={handleUpdate}
                                                            disabled={!selectedFile || isLoading}
                                                        >
                                                            Update
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </form>
                                    </div>

                                    {/* Import Results */}
                                    {importResults && (
                                        <div className="card card-primary card-outline mt-3">
                                            <div className="card-header">
                                                <h3 className="card-title">
                                                    {resultMode === 'update' ? 'Update Results' : 'Import Results'}
                                                </h3>
                                            </div>
                                            <div className="card-body import-results">
                                                {importResults.resultMessage ? (
                                                    <div
                                                        className={`alert ${Number(importResults.failed || 0) > 0 ? 'alert-warning' : 'alert-success'} mb-3`}
                                                    >
                                                        {importResults.resultMessage}
                                                    </div>
                                                ) : null}
                                                <div className="row mb-3">
                                                    <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
                                                        <div className="info-box">
                                                            <span className="info-box-icon bg-info elevation-1"><i className="fa-solid fa-list"></i></span>
                                                            <div className="info-box-content">
                                                                <span className="info-box-text">Total Records</span>
                                                                <span className="info-box-number">{importResults.total || 0}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
                                                        <div className="info-box">
                                                            <span className="info-box-icon bg-primary elevation-1"><i className="fa-solid fa-plus"></i></span>
                                                            <div className="info-box-content">
                                                                <span className="info-box-text">Created</span>
                                                                <span className="info-box-number">
                                                                    {importResults.created || importResults.createdItems?.length || importResults.imported?.length || 0}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
                                                        <div className="info-box">
                                                            <span className="info-box-icon bg-success elevation-1"><i className="fa-solid fa-check"></i></span>
                                                            <div className="info-box-content">
                                                                <span className="info-box-text">Updated</span>
                                                                <span className="info-box-number">{importResults.updated || 0}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
                                                        <div className="info-box">
                                                            <span className="info-box-icon bg-warning elevation-1"><i className="fa-solid fa-exclamation"></i></span>
                                                            <div className="info-box-content">
                                                                <span className="info-box-text">Skipped Records</span>
                                                                <span className="info-box-number">{importResults.skipped || 0}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
                                                        <div className="info-box">
                                                            <span className="info-box-icon bg-danger elevation-1"><i className="fa-solid fa-times"></i></span>
                                                            <div className="info-box-content">
                                                                <span className="info-box-text">Failed Records</span>
                                                                <span className="info-box-number">{importResults.failed || 0}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {(importResults.createdItems?.length > 0 || importResults.imported?.length > 0) ? (
                                                    <div className="mb-4">
                                                        <h5 className="mb-3 text-success">Created Records:</h5>
                                                        <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #dee2e6' }}>
                                                            <table className="table table-bordered table-striped mb-0">
                                                                <thead className="thead-light" style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#f8f9fa' }}>
                                                                    <tr>
                                                                        <th>Row</th>
                                                                        <th>PO No</th>
                                                                        <th>Material Code</th>
                                                                        <th>Customer Name</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {(importResults.createdItems || importResults.imported).map((record, index) => (
                                                                        <tr key={record.id || index}>
                                                                            <td>{record.row || index + 1}</td>
                                                                            <td>{record.poNo || '-'}</td>
                                                                            <td>{record.materialCode || '-'}</td>
                                                                            <td>{record.customerName || '-'}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                ) : null}

                                                {importResults.updatedItems?.length > 0 ? (
                                                    <div className="mb-4">
                                                        <h5 className="mb-3 text-success">Updated Records:</h5>
                                                        <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #dee2e6' }}>
                                                            <table className="table table-bordered table-striped mb-0">
                                                                <thead className="thead-light" style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#f8f9fa' }}>
                                                                    <tr>
                                                                        <th>Row</th>
                                                                        <th>PO No</th>
                                                                        <th>Material Code</th>
                                                                        <th>Customer Name</th>
                                                                        <th>PO Status</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {importResults.updatedItems.map((record, index) => (
                                                                        <tr key={index}>
                                                                            <td>{record.row || index + 1}</td>
                                                                            <td>{record.poNo || '-'}</td>
                                                                            <td>{record.materialCode || '-'}</td>
                                                                            <td>{record.customerName || '-'}</td>
                                                                            <td>{record.poStatus || record.projectStatus || '-'}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                ) : null}

                                                {importResults.skippedItems?.length > 0 ? (
                                                    <div className="mb-4">
                                                        <h5 className="mb-3 text-warning">Skipped Records:</h5>
                                                        <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #dee2e6' }}>
                                                            <table className="table table-bordered table-striped mb-0">
                                                                <thead className="thead-light" style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#f8f9fa' }}>
                                                                    <tr>
                                                                        <th>Row</th>
                                                                        <th>PO No</th>
                                                                        <th>Material Code</th>
                                                                        <th>Reason</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {importResults.skippedItems.map((record, index) => (
                                                                        <tr key={index}>
                                                                            <td>{record.row || index + 1}</td>
                                                                            <td>{record.poNo || '-'}</td>
                                                                            <td>{record.materialCode || '-'}</td>
                                                                            <td>{record.reason || '-'}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                ) : null}

                                                {Number(importResults.failed || 0) > 0 && importResults.errors?.length > 0 ? (
                                                    <div>
                                                        <h5 className="mb-3 text-danger">Errors:</h5>
                                                        <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #dee2e6' }}>
                                                            <table className="table table-bordered table-striped mb-0">
                                                                <thead className="thead-light" style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#f8f9fa' }}>
                                                                    <tr>
                                                                        <th>Row</th>
                                                                        <th>PO No</th>
                                                                        <th>Material Code</th>
                                                                        <th>Customer Name</th>
                                                                        <th>Error</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {importResults.errors.map((error, index) => (
                                                                        <tr key={index}>
                                                                            <td>{error.row || index + 1}</td>
                                                                            <td>{error.poNo || '-'}</td>
                                                                            <td>{error.materialCode || '-'}</td>
                                                                            <td>{error.customerName || '-'}</td>
                                                                            <td className="text-danger">{error.error || error.message || error.reason || '-'}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {showDuplicateModal && (
                <div
                    tabIndex="-1"
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        width: '100vw',
                        height: '100vh',
                        background: 'rgba(0,0,0,.45)',
                        zIndex: 10500,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '16px',
                        boxSizing: 'border-box'
                    }}
                >
                    <div
                        className="modal-content"
                        style={{
                            maxWidth: '900px',
                            width: '100%',
                            margin: 0,
                            maxHeight: '90vh',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        <div
                            className="modal-header text-white"
                            style={{ backgroundColor: '#17a2b8' }}
                        >
                            <h5 className="modal-title mb-0">
                                <i className="fa fa-exclamation-triangle mr-2" />
                                {modalMode === 'missing'
                                    ? 'No matching records found'
                                    : 'Duplicate records found'}
                            </h5>
                            <button
                                type="button"
                                className="close text-white"
                                onClick={handleCancelDuplicates}
                                disabled={isLoading}
                            >
                                <span>&times;</span>
                            </button>
                        </div>
                        <div className="modal-body" style={{ overflowY: 'auto' }}>
                            <p className="mb-3 text-danger">
                                {modalMode === 'missing' ? (
                                    <>
                                        {duplicateCount} row(s) have no matching tracker for{' '}
                                        <strong>PO No</strong> + <strong>Customer Name</strong> +{' '}
                                        <strong>Material Code</strong>.
                                    </>
                                ) : (
                                    <>
                                        {duplicateCount} row(s) already exist with the same{' '}
                                        <strong>PO No</strong>, <strong>Customer Name</strong> and{' '}
                                        <strong>Material Code</strong>.
                                    </>
                                )}
                            </p>
                            <div
                                className="table-responsive"
                                style={{ maxHeight: '320px', overflowY: 'auto' }}
                            >
                                <table className="table table-bordered table-hover mb-0">
                                    <thead className="thead-light">
                                        <tr>
                                            <th>Row</th>
                                            <th>PO No</th>
                                            <th>Customer Name</th>
                                            <th>Material Code</th>
                                            <th>{modalMode === 'missing' ? 'Reason' : 'Warning'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {duplicateWarnings.length > 0 ? (
                                            duplicateWarnings.map((item, index) => (
                                                <tr key={`${item.existingId || item.poNo || 'dup'}-${index}`}>
                                                    <td>{item.row || index + 1}</td>
                                                    <td>{item.poNo || '-'}</td>
                                                    <td>{item.customerName || '-'}</td>
                                                    <td>{item.materialCode || '-'}</td>
                                                    <td>{item.warning || item.message || 'Already exists'}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={5} className="text-center text-muted">
                                                    Duplicate rows were detected. Proceed to update them.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={handleCancelDuplicates}
                                disabled={isLoading}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-info"
                                onClick={handleConfirmDuplicates}
                                disabled={isLoading}
                            >
                                {isLoading && loadingAction === 'confirm' ? (
                                    <>
                                        <PulseLoader color="#fff" size={8} />
                                        <span className="ml-2">
                                            {modalMode === 'missing' ? 'Adding...' : 'Updating...'}
                                        </span>
                                    </>
                                ) : modalMode === 'missing' ? (
                                    'Yes, add records'
                                ) : (
                                    'Yes, proceed'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default TrackerUpdateAdd